import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SalesOrder } from '../types/interfaces';
import '../App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';

const SalesOrderList: React.FC = () => {
  const navigate = useNavigate();
  const [salesOrders, setSalesOrders] = useState<SalesOrder[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadOrders = async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/sales-orders?page=${page}&limit=10`, {
        headers: { Accept: 'application/json' },
        signal,
      });
      if (!res.ok) {
        throw new Error(`Failed to fetch sales orders: HTTP ${res.status}`);
      }
      const data = await res.json();
      if (signal?.aborted) return;
      setSalesOrders(data.orders || []);
      setTotalPages(data.totalPages || 1);
      setError(null);
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      console.error('fetchSalesOrders error:', err);
      setError('Failed to load sales orders: ' + err.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    const abort = new AbortController();
    void loadOrders(abort.signal);
    return () => abort.abort();
  }, [page]);

  const handleDelete = async (order: SalesOrder) => {
    if (order.status !== 'Draft') return;
    const ok = window.confirm(
      `Delete Draft sales order #${order.orderId}? This cannot be undone.`
    );
    if (!ok) return;
    setDeletingId(order.orderId);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/sales-orders/${order.orderId}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      await loadOrders();
    } catch (err: any) {
      setError('Failed to delete sales order: ' + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="page-container container">
      <h2 className="app-header text-warning mb-4">Sales Orders</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      <button
        onClick={() => navigate('/sales-orders/new')}
        className="btn btn-primary mb-4"
      >
        Create New Sales Order
      </button>
      {loading ? (
        <p className="text-muted">Loading sales orders…</p>
      ) : salesOrders.length === 0 ? (
        <p>No sales orders found.</p>
      ) : (
        <>
          <table className="inventory-table table table-striped">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>PO Number</th>
                <th>Status</th>
                <th>Created Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {salesOrders.map((order) => (
                <tr key={order.orderId}>
                  <td>{order.orderId}</td>
                  <td>{order.customerName}</td>
                  <td>{order.poNumber || '-'}</td>
                  <td>{order.status}</td>
                  <td>{order.createdDate}</td>
                  <td>
                    <div className="d-flex gap-2 flex-wrap">
                      <button
                        onClick={() => navigate(`/sales-orders/${order.orderId}`)}
                        className="btn btn-primary btn-sm"
                      >
                        View
                      </button>
                      {order.status === 'Draft' && (
                        <button
                          type="button"
                          onClick={() => handleDelete(order)}
                          className="btn btn-outline-danger btn-sm"
                          disabled={deletingId === order.orderId}
                          aria-label={`Delete draft sales order ${order.orderId}`}
                        >
                          {deletingId === order.orderId ? 'Deleting…' : 'Delete Draft'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="order-list">
            {salesOrders.map((order) => (
              <div key={order.orderId} className="order-card card mb-2">
                <div className="card-body">
                  <h5 className="card-title">Order {order.orderId}</h5>
                  <p className="card-text">Customer: {order.customerName}</p>
                  <p className="card-text">PO: {order.poNumber || '-'}</p>
                  <p className="card-text">Status: {order.status}</p>
                  <p className="card-text">Date: {order.createdDate}</p>
                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      onClick={() => navigate(`/sales-orders/${order.orderId}`)}
                      className="btn btn-primary btn-sm"
                    >
                      View
                    </button>
                    {order.status === 'Draft' && (
                      <button
                        type="button"
                        onClick={() => handleDelete(order)}
                        className="btn btn-outline-danger btn-sm"
                        disabled={deletingId === order.orderId}
                        aria-label={`Delete draft sales order ${order.orderId}`}
                      >
                        {deletingId === order.orderId ? 'Deleting…' : 'Delete Draft'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="d-flex justify-content-between mt-3">
            <button
              className="btn btn-secondary"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <span>Page {page} of {totalPages}</span>
            <button
              className="btn btn-secondary"
              onClick={() => setPage(p => p + 1)}
              disabled={page === totalPages}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SalesOrderList;